import classNames from "classnames"
import type { Transition } from "motion/react"
import * as m from "motion/react-m"

import Tab from "./Tab"
import TabContent from "./TabContent"
import { Color } from "../../../../constants/V2/color"
import DownloadAppButton from "../../../elements/V2/Buttons/DownloadAppButton"
import PillButton from "../../../elements/V2/Buttons/PillButton"
import GatsbyStoryBlokImage from "../../../elements/V2/GatsbyStoryblokImage"
import Video from "../../../experiments/Video"
import GridSection from "../../Layouts/GridSection"

import Typography from "@components/elements/V2/Typography"
import { LayoutHeaderProps } from "@components/V2/LayoutComponents/Header"
import { getAccentColor } from "@utils/V2/color"
import React, { useEffect, useRef, useState } from "react"

const IMAGE_TRANSITION: Transition = { duration: 0.3, ease: "easeInOut" }

interface BenefitDriverTab {
  leadText: string
  bodyText: string
  image: string
  imageAlt: string
  video?: string
  ctaText?: string
  ctaLink?: string | YocoCom.YocoLink
}

type Props = {
  mode: "light" | "dark"
  tabs: BenefitDriverTab[]
  buttonText: string
  buttonUrl: string | YocoCom.YocoLink | undefined
  showDownloadAppButton: boolean
  description?: string
  isNumbered?: boolean
  mobileHeadingAlignment?: "left" | "center"
  desktopLabelAlignment?: "left" | "right"
  centerLabels?: boolean
  id?: string
  heading?: LayoutHeaderProps
  trackingEvent?: string
  trackingEventKey?: string
  trackingEventValue?: string
}

const BenefitDriver = ({
  mode,
  heading,
  description,
  tabs,
  buttonText,
  buttonUrl,
  showDownloadAppButton = false,
  isNumbered = false,
  trackingEvent,
  trackingEventKey,
  trackingEventValue,

  desktopLabelAlignment = "left",
  centerLabels = false,
  id,
  ...props
}: Props) => {
  const [activeTab, setActiveTab] = useState(0)
  const targetRefs = useRef<(HTMLDivElement | null)[]>([])
  const isHovering = useRef(false)

  function isActive(index: number) {
    if (activeTab === index) return true
  }

  const backgroundColor = mode === "light" ? Color.White : Color.Charcoal
  const accentColor = getAccentColor(backgroundColor)
  const hasHeaderContent = Boolean(
    heading?.superscriptText || heading?.headingText || description
  )

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isHovering.current) {
            const target = entry.target as HTMLElement
            const index = target.dataset.index

            if (index !== undefined) {
              setActiveTab(Number(index))
            }
          }
        })
      },
      {
        rootMargin: "-50% 0px -50% 0px",
        threshold: 0,
      }
    )

    targetRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [])

  return (
    <GridSection id={id} primaryBackgroundColor={backgroundColor} {...props}>
      <div
        className={classNames(
          "relative order-2 col-span-full w-full flex-col sm:col-span-2 sm:flex md:col-span-3 lg:col-span-6",
          { "items-center justify-center": centerLabels },
          desktopLabelAlignment === "right" ? "order-2" : "sm:order-1"
        )}
      >
        <div
          onMouseEnter={() => {
            isHovering.current = true
          }}
          onMouseLeave={() => {
            isHovering.current = false
          }}
          className={classNames(
            "relative hidden flex-col pb-[30vh] sm:flex",
            { "items-center justify-center": centerLabels },
            desktopLabelAlignment === "right" ? "pl-xl pt-xl pb-xl" : "p-xl"
          )}
        >
          {/* Text content */}
          {tabs.map((tab, index) => (
            <React.Fragment key={`tab-group-${index}`}>
              <div
                data-index={index}
                ref={(el) => (targetRefs.current[index] = el)}
                className="w-full"
                onMouseEnter={() => setActiveTab(index)}
              >
                {index === 0 && hasHeaderContent && (
                  <div
                    className={classNames(
                      "mt-104",
                      isActive(0) ? "opacity-100" : "opacity-30"
                    )}
                  >
                    {heading?.superscriptText && (
                      <Typography
                        text={heading.superscriptText}
                        size="subscript-lg"
                        weight="book"
                        font="grotesk"
                        element="p"
                        color={accentColor}
                        className="mb-8"
                      />
                    )}

                    {heading?.headingText && (
                      <Typography
                        text={heading.headingText}
                        size="h3"
                        weight="book"
                        font="grotesk"
                        element="h3"
                        color={accentColor}
                      />
                    )}

                    {description && (
                      <Typography
                        text={description}
                        size="body-lg"
                        weight="book"
                        font="grotesk"
                        element="p"
                        color={accentColor}
                        className="mt-16"
                      />
                    )}
                  </div>
                )}

                <Tab
                  active={isActive(index)}
                  bodyText={tab.bodyText}
                  leadText={tab.leadText}
                  textColor={accentColor}
                  number={isNumbered ? index + 1 : undefined}
                  ctaText={tab.ctaText}
                  ctaLink={tab.ctaLink}
                />
              </div>

              {index !== tabs.length - 1 && (
                <span
                  className={classNames(
                    "h-[1px] w-full",
                    backgroundColor ? accentColor : "bg-charcoal/30"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {showDownloadAppButton ||
          (buttonText && buttonUrl && (
            <div className="p-xl">
              {showDownloadAppButton ? (
                <DownloadAppButton
                  style="bordered"
                  text={buttonText}
                  color={Color.Blue}
                  overrideTextColor={accentColor}
                />
              ) : (
                <>
                  {buttonText && buttonUrl ? (
                    <PillButton
                      text={buttonText}
                      linkUrl={buttonUrl}
                      color={Color.Blue}
                      style="bordered"
                      size="large"
                      overrideTextColor={accentColor}
                      trackingEvent={trackingEvent}
                      trackingEventKey={trackingEventKey}
                      trackingEventValue={trackingEventValue}
                    />
                  ) : null}
                </>
              )}
            </div>
          ))}
      </div>

      <div
        className={classNames(
          "relative col-span-full hidden sm:order-1 sm:col-span-2 sm:grid md:col-span-3 lg:col-span-6",
          desktopLabelAlignment === "right"
            ? "order-1 items-stretch"
            : "sm:order-2"
        )}
      >
        <div className="sticky top-44 grid h-[70vh]">
          {tabs.map((tab, index) => (
            <m.div
              key={`tab-image-${index}`}
              className="sticky col-start-1 row-start-1 h-fit"
              animate={{ opacity: isActive(index) ? 1 : 0 }}
              transition={IMAGE_TRANSITION}
            >
              {tab.video ? (
                <Video
                  video={tab.video}
                  coverImage={tab.image}
                  ImageAlt={tab.imageAlt || "Benefit tab image"}
                  rounded
                  reset={index !== activeTab}
                  pause={index !== activeTab}
                  playbackMode="autoplay"
                  aspectRatio="1:1" //TODO make this a permanent change if experiment signed off
                  width={desktopLabelAlignment === "right" ? undefined : 600}
                  className={classNames({
                    "object-fill": desktopLabelAlignment === "right",
                  })}
                />
              ) : (
                <GatsbyStoryBlokImage
                  image={tab.image}
                  alt={tab.imageAlt || "Benefit tab image"}
                  rounded
                  aspectRatio="1:1"
                  width={600}
                  className="w-full"
                />
              )}
            </m.div>
          ))}
        </div>
      </div>

      {/* Mobile */}
      <div className="col-span-full flex flex-col gap-40 sm:hidden">
        {tabs.map((tab, index) => (
          <div key={`tab-${index}`} className="flex flex-col gap-20">
            {index === 0 && hasHeaderContent && (
              <div>
                {heading?.superscriptText && (
                  <Typography
                    text={heading.superscriptText}
                    size="subscript-lg"
                    weight="book"
                    font="grotesk"
                    element="p"
                    color={accentColor}
                    className="mb-8"
                  />
                )}

                {heading?.headingText && (
                  <Typography
                    text={heading.headingText}
                    size="h3"
                    weight="book"
                    font="grotesk"
                    element="h3"
                    color={accentColor}
                  />
                )}

                {description && (
                  <Typography
                    text={description}
                    size="body-lg"
                    weight="book"
                    font="grotesk"
                    element="p"
                    color={accentColor}
                    className="mt-16"
                  />
                )}
              </div>
            )}

            {tab.video ? (
              <Video
                key={`tab-image-${index}`}
                video={tab.video}
                coverImage={tab.image}
                ImageAlt={tab.imageAlt || "Benefit tab image"}
                aspectRatio="1:1"
                width={600}
                preventLoop
                rounded
                playbackMode="autoplay"
                reset={index !== activeTab}
                pause={index !== activeTab}
              />
            ) : (
              <GatsbyStoryBlokImage
                key={`tab-image-${index}`}
                image={tab.image}
                alt={tab.imageAlt || "Benefit tab image"}
                aspectRatio="1:1"
                rounded
                className="h-auto w-full"
              />
            )}

            <TabContent
              variant="mobile"
              textColor={accentColor}
              leadText={tab.leadText}
              bodyText={tab.bodyText}
              number={isNumbered ? index + 1 : undefined}
              ctaText={tab.ctaText}
              ctaLink={tab.ctaLink}
            />
          </div>
        ))}
      </div>
    </GridSection>
  )
}

export default BenefitDriver
